import { useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer, LayersControl, TileLayer, FeatureGroup,
  Polygon, Popup, Circle, Marker, GeoJSON, ScaleControl,
} from "react-leaflet";
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { image } from "../../api";
import { useLocation } from "react-router-dom";
import { boundaryCRUD, mapCRUD } from "../../actions/boundary";
import { buildingCRUD } from "../../actions/building";
import { LAYER_CONFIG, TILE_LAYERS, BUILDING_COLORS } from './mapConstants';
import { buildBuildingPopup, BuildingDetailPopup } from './popupHelpers';
import { MapLegend } from './MapLegend';
import { LayerPanel } from './LayerPanel';
import { HeatmapLayer } from './HeatmapLayer';
import { BuildingDraw } from './useBuildingDraw';
import { toast } from "react-toastify";


export const MapContent = (props) => {
  const { value, category, listValue, typeData, boundaryFilter } = props;
  const { BaseLayer } = LayersControl;

  const defaultIcon = new Icon({
    iconUrl: "https://icons.iconarchive.com/icons/paomedia/small-n-flat/512/map-marker-icon.png",
    iconAnchor: [25, 40],
    iconSize: [50, 50],
  });

  let location = useLocation();
  const mapRef = useRef();
  let popupRef = useRef();

  const [refReady, setRefReady] = useState(false);
  // The requesting user's scope (from /scope/me): boundary to fly to + highlight.
  const [scope, setScope] = useState(null);
  const [dataType] = useState(location.state ? location.state.type : null);
  const [dataSubType] = useState(location.state ? location.state.subType : null);
  const [dataId] = useState(location.state ? location.state.id : null);
  const [dataOne, setDataOne] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Boundary layer data
  const [island, setIsland] = useState([]);
  const [region, setRegion] = useState([]);
  const [district, setDistrict] = useState([]);
  const [shehia, setShehia] = useState([]);
  const [zone, setZone] = useState([]);
  const [building, setBuilding] = useState([]);

  // Filter boundary overlay (shown when cascading filter selects a boundary)
  const [filterBoundary, setFilterBoundary] = useState(null);

  // Overlay toggle states
  const [layerStates, setLayerStates] = useState({
    island: false, region: false, district: false, shehia: false, zone: false,
  });
  const [buildingCheck, setBuildingCheck] = useState(false);
  const [heatmapCheck, setHeatmapCheck] = useState(false);

  // Building -> member count map, fetched once buildings are loaded
  const [memberCounts, setMemberCounts] = useState({});

  // Drawing mode (only when a single building is opened from the list).
  const isSingleBuilding = (dataType === 'building' || typeData === 'building') && (dataId || listValue);
  const [drawMode, setDrawMode] = useState(!!(location.state && location.state.draw));
  const [drawnGeoJson, setDrawnGeoJson] = useState(null);
  const [savingPolygon, setSavingPolygon] = useState(false);

  // Existing polygon of the opened building (GeoJSON geometry, lng/lat) for pre-load.
  const existingPolygon = (dataOne && dataOne[0] && dataOne[0].building_polygon) || null;

  const saveDrawnPolygon = () => {
    const bId = listValue ? listValue : dataId;
    if (!bId || !drawnGeoJson) {
      toast.error("Draw the building outline first.", { autoClose: 4000 });
      return;
    }
    setSavingPolygon(true);
    buildingCRUD.updateBuilding(bId, { building_polygon: drawnGeoJson })
      .then(() => {
        toast.success("Building outline saved.", { autoClose: 5000 });
        setDrawMode(false);
        // Refresh the displayed building geometry.
        buildingCRUD.loadOneBuildingMap(bId).then((r) => setDataOne(r.data)).catch(() => {});
      })
      .catch((e) => toast.error(e?.response?.data?.building_polygon?.[0] || "Failed to save outline", { autoClose: 6000 }))
      .finally(() => setSavingPolygon(false));
  };

  // Load single feature when navigating from list
  useEffect(() => {
    setLoading(true);
    setDataOne([]);
    if (dataType === 'building' || typeData === 'building') {
      buildingCRUD.loadOneBuildingMap(listValue ? listValue : dataId).then((resp) => {
          setDataOne(resp.data);
          const b = resp.data && resp.data[0];
          if (b && b.building_centroid) {
            const [lng, lat] = b.building_centroid.coordinates;
            mapRef.current?.flyTo([lat, lng], 20);
          } else if (b && b.boundary_centroid) {
            // No own point yet (e.g. about to draw): fly to its Shehia centroid.
            mapRef.current?.flyTo([b.boundary_centroid.lat, b.boundary_centroid.lng], 17);
          }
          if (refReady) popupRef.openOn(mapRef.current);
        }).finally(() => setLoading(false));
    } else if (dataType === 'boundary' || typeData === 'boundary') {
      const bId = listValue ? listValue : dataId;
      if (!bId) { setLoading(false); return; }
      boundaryCRUD.loadOneBoundaryMap(bId).then((resp) => {
          setDataOne(resp.data);
          const bdy = resp.data && resp.data[0];
          if (bdy && bdy.boundary_centroid) {
            const [lng, lat] = bdy.boundary_centroid.coordinates;
            mapRef.current?.flyTo([lat, lng], 15);
          }
          if (refReady) popupRef.openOn(mapRef.current);
        }).finally(() => setLoading(false));
    } else if (dataType === 'island') {
      boundaryCRUD.loadIslandMap(listValue ? listValue : dataId).then((resp) => {
          setDataOne(resp.data);
          const isl = resp.data[0];
          if (isl && isl.island_centroid) {
            const [lng, lat] = isl.island_centroid.coordinates;
            mapRef.current.flyTo([lat, lng], 11);
          }
          if (refReady) popupRef.openOn(mapRef.current);
        }).finally(() => setLoading(false));
    }
  }, [dataType, dataId, listValue]);

  // Helper: load buildings with optional boundary filter
  const loadBuildings = (filterId) => {
    if (filterId) {
      return buildingCRUD.loadSpecificBuildingJson(filterId).then(r => setBuilding(r.data));
    }
    return buildingCRUD.loadBuildingJson().then(r => setBuilding(r.data));
  };

  // Fetch member counts once buildings are visible. Fails silently if the
  // backend endpoint isn't deployed yet — popups fall back to "—".
  const loadMemberCounts = (filterId) => {
    buildingCRUD
      .loadBuildingMembersCount(filterId)
      .then((r) => setMemberCounts(r.data || {}))
      .catch(() => setMemberCounts({}));
  };

  // Load all layer data
  useEffect(() => {
    setLoading(true);
    const loadBoundaryLayers = () => {
      mapCRUD.loadIslandJson().then(r => setIsland(r.data)).catch(() => {});
      mapCRUD.loadRegionJson().then(r => setRegion(r.data)).catch(() => {});
      mapCRUD.loadDistrictJson().then(r => setDistrict(r.data)).catch(() => {});
      mapCRUD.loadShehiaJson().then(r => setShehia(r.data)).catch(() => {});
      mapCRUD.loadZoneJson().then(r => setZone(r.data)).catch(() => {}).finally(() => setLoading(false));
    };

    if (dataType === 'boundary') {
      if (dataSubType === 'region') {
        mapCRUD.loadRegionJson().then(r => setRegion(r.data));
        mapCRUD.loadSpecificDistrictJson(dataId).then(r => setDistrict(r.data));
        mapCRUD.loadShehiaJson().then(r => setShehia(r.data));
        mapCRUD.loadZoneJson().then(r => setZone(r.data)).finally(() => setLoading(false));
      } else if (dataSubType === 'district') {
        mapCRUD.loadRegionJson().then(r => setRegion(r.data));
        mapCRUD.loadDistrictJson().then(r => setDistrict(r.data));
        mapCRUD.loadSpecificShehiaJson(dataId).then(r => setShehia(r.data));
        mapCRUD.loadZoneJson().then(r => setZone(r.data)).finally(() => setLoading(false));
      } else if (dataSubType === 'shehia') {
        mapCRUD.loadRegionJson().then(r => setRegion(r.data));
        mapCRUD.loadDistrictJson().then(r => setDistrict(r.data));
        mapCRUD.loadShehiaJson().then(r => setShehia(r.data));
        mapCRUD.loadSpecificZoneJson(dataId).then(r => setZone(r.data)).finally(() => setLoading(false));
      } else if (dataSubType === 'zone') {
        mapCRUD.loadRegionJson().then(r => setRegion(r.data));
        mapCRUD.loadDistrictJson().then(r => setDistrict(r.data));
        mapCRUD.loadShehiaJson().then(r => setShehia(r.data));
        mapCRUD.loadZoneJson().then(r => setZone(r.data)).finally(() => setLoading(false));
      } else {
        loadBoundaryLayers();
      }
    } else {
      loadBoundaryLayers();
    }
  }, [value, category, boundaryFilter]);

  // Lazy-load buildings only when building or heatmap layer is toggled on
  const needsBuildingData = buildingCheck || heatmapCheck;
  useEffect(() => {
    if (needsBuildingData && (!building || !building.features || building.features.length === 0)) {
      loadBuildings(boundaryFilter).catch(() => {});
      loadMemberCounts(boundaryFilter);
    }
  }, [needsBuildingData]);

  // Reload buildings when boundary filter changes (if buildings are visible)
  useEffect(() => {
    if (needsBuildingData) {
      loadBuildings(boundaryFilter).catch(() => {});
      loadMemberCounts(boundaryFilter);
    }
  }, [boundaryFilter]);

  // Load and fly to filter boundary when cascading filter changes
  useEffect(() => {
    if (!boundaryFilter) {
      setFilterBoundary(null);
      return;
    }
    mapCRUD.loadShehiaById(boundaryFilter).then((resp) => {
      if (resp.data && resp.data.features && resp.data.features.length > 0) {
        setFilterBoundary(resp.data);
        // Fly to the boundary centroid
        const feature = resp.data.features[0];
        if (feature.geometry && feature.geometry.coordinates) {
          const coords = feature.geometry.coordinates;
          // Get rough center from first polygon ring
          try {
            const ring = coords[0][0] || coords[0];
            const lats = ring.map(c => c[1]);
            const lngs = ring.map(c => c[0]);
            const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
            const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
            mapRef.current?.flyTo([centerLat, centerLng], 15);
          } catch(e) {}
        }
      }
    }).catch(() => setFilterBoundary(null));
  }, [boundaryFilter]);

  // Scope: on open, a scoped user (e.g. Shehia leader) flies to their own
  // boundary and we keep its polygon to highlight. Admins are national -> no fly.
  // Skip the fly when navigating to a specific feature (the other effect handles it).
  useEffect(() => {
    boundaryCRUD.loadScopeMe()
      .then((resp) => {
        const s = resp.data;
        setScope(s);
        if (!s || s.is_national || dataType || typeData) return;
        if (s.centroid && mapRef.current) {
          const zoomFor = { Region: 11, District: 12, Shehia: 14, Zone: 16 };
          mapRef.current.flyTo([s.centroid.lat, s.centroid.lng], zoomFor[s.level] || 13);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Zoom tracking
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.on("zoomend", function () {
        setZoom(mapRef.current._zoom);
      });
    }
  }, [mapRef.current]);

  // Fullscreen change listener
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // --- Layer toggle handler ---
  const handleLayerToggle = (key) => {
    setLayerStates(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const handleBuildingToggle = () => setBuildingCheck(prev => !prev);
  const handleHeatmapToggle = () => setHeatmapCheck(prev => !prev);

  // --- Fullscreen toggle ---
  const toggleFullscreen = () => {
    const container = mapRef.current?.getContainer();
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen();
    }
  };

  // --- Get all descendant boundary IDs for a given boundary ---
  const getDescendantIds = (boundaryId) => {
    const ids = new Set([boundaryId]);
    // Collect all loaded boundary features from all levels
    const allBoundaries = [region, district, shehia, zone]
      .filter(d => d && d.features)
      .flatMap(d => d.features);

    // BFS: find children at each level
    let queue = [boundaryId];
    while (queue.length > 0) {
      const nextQueue = [];
      for (const feat of allBoundaries) {
        const parentId = feat.properties.boundary_parent;
        if (parentId && queue.includes(parentId) && !ids.has(feat.properties.id)) {
          ids.add(feat.properties.id);
          nextQueue.push(feat.properties.id);
        }
      }
      queue = nextQueue;
    }
    return ids;
  };

  // --- Count buildings from loaded GeoJSON for a boundary and all its descendants ---
  const countBuildingsForBoundary = (boundaryId) => {
    if (!building || !building.features) return { total: 0, residential: 0, commercial: 0, civic: 0, religious: 0, mixed: 0 };
    const ids = getDescendantIds(boundaryId);
    const features = building.features.filter(f => ids.has(f.properties.boundary_id_id));
    return {
      total: features.length,
      residential: features.filter(f => f.properties.dictionary_building_category_id === 25).length,
      commercial: features.filter(f => f.properties.dictionary_building_category_id === 26).length,
      civic: features.filter(f => f.properties.dictionary_building_category_id === 27).length,
      religious: features.filter(f => f.properties.dictionary_building_category_id === 28).length,
      mixed: features.filter(f => f.properties.dictionary_building_category_id === 29).length,
    };
  };

  // --- Build rich popup HTML for a boundary ---
  const buildRichBoundaryPopup = (props, counts) => {
    const name = props.boundary_name || props.name || 'N/A';
    const code = props.boundary_code || '';
    const postcode = props.postcode || '';
    const initials = props.boundary_initials || '';

    const catRow = (color, label, count) =>
      '<div style="display:flex; justify-content:space-between; margin-bottom:2px;">' +
        '<span><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:' + color + ';margin-right:4px;"></span>' + label + '</span>' +
        '<span style="font-weight:600;">' + count.toLocaleString() + '</span>' +
      '</div>';

    return (
      '<div style="width:280px; font-size:13px;">' +
      '<div style="font-size:15px; font-weight:bold; margin-bottom:6px; color:#405189;">' + name + '</div>' +
      (initials ? '<div><span style="font-weight:600;">Initials:</span> ' + initials + '</div>' : '') +
      (code ? '<div><span style="font-weight:600;">Code:</span> ' + code + '</div>' : '') +
      (postcode ? '<div><span style="font-weight:600;">Postcode:</span> ' + postcode + '</div>' : '') +
      (counts && counts.total > 0 ? (
        '<hr style="margin:6px 0;"/>' +
        '<div style="font-weight:600; margin-bottom:4px;">Buildings (' + counts.total.toLocaleString() + ' registered)</div>' +
        catRow('#2196F3', 'Residential', counts.residential) +
        catRow('#FF9800', 'Commercial', counts.commercial) +
        catRow('#9C27B0', 'Civic', counts.civic) +
        catRow('#4CAF50', 'Religious', counts.religious) +
        catRow('#F44336', 'Mixed', counts.mixed)
      ) : '') +
      '</div>'
    );
  };

  // --- onEachFeature factory for boundary layers ---
  const createBoundaryHandler = (defaultColor) => (feature, layer) => {
    const props = feature.properties;
    const boundaryId = props.id;
    const counts = countBuildingsForBoundary(boundaryId);

    layer.bindPopup(buildRichBoundaryPopup(props, counts), { maxWidth: 320, minWidth: 280 });
    layer.on({
      mouseover: () => layer.setStyle({ weight: 2, color: '#ed071a', fillColor: 'transparent', fillOpacity: 1 }),
      mouseout: () => layer.setStyle({ color: defaultColor, weight: 4, fillColor: 'transparent', fillOpacity: 1 }),
    });
  };

  // --- onEachFeature for buildings ---
  const bFeature = (feature, layer) => {
    const count = memberCounts ? memberCounts[feature.properties.id] : undefined;
    layer.bindPopup(buildBuildingPopup(feature.properties, count), { maxWidth: 320, minWidth: 280 });
    layer.on({
      mouseover: () => layer.setStyle({ weight: 0.9, color: '#ed071a', fillColor: 'transparent', fillOpacity: 0.2 }),
      mouseout: () => {
        const color = BUILDING_COLORS[feature.properties.dictionary_building_category_id] || '#dddddd';
        layer.setStyle({ color, weight: zoom >= 15 ? 1 : 2, fillColor: color, fillOpacity: 0.3 });
      },
    });
  };

  // --- Build GeoJSON layer for a boundary type ---
  const dataMap = { island, region, district, shehia, zone };
  const buildBoundaryLayer = (key) => {
    const config = LAYER_CONFIG.find(l => l.key === key);
    const data = dataMap[key];
    if (!data || (data.numberReturned === undefined && !data.features)) return null;
    return (
      <FeatureGroup key={key}>
        <GeoJSON
          data={data}
          style={() => ({ color: config.color, weight: 4, fillColor: 'transparent', fillOpacity: 1 })}
          onEachFeature={createBoundaryHandler(config.color)}
        />
      </FeatureGroup>
    );
  };

  // --- Building layer with per-category colors ---
  const buildBuildingLayerElement = () => {
    if (!building || (building.numberReturned === undefined && !building.features)) return null;
    return (
      <FeatureGroup>
        <GeoJSON
          data={building}
          style={(feature) => {
            const color = BUILDING_COLORS[feature.properties.dictionary_building_category_id] || '#dddddd';
            return { color, weight: zoom >= 15 ? 1 : 2, fillColor: color, fillOpacity: 0.3 };
          }}
          onEachFeature={bFeature}
        />
      </FeatureGroup>
    );
  };

  // --- Main layer selected from dropdown ---
  const valueLayerMap = { '1': 'island', '2': 'region', '3': 'district', '4': 'shehia', '5': 'zone' };
  let mainLayer = null;
  let mainBuildingLayer = null;

  const mainKey = valueLayerMap[String(value)];
  if (mainKey) {
    mainLayer = buildBoundaryLayer(mainKey);
    if (!mainLayer) mainLayer = <div><div className="loading">Loading&#8230;</div></div>;
  } else if (value === 6 || value === '6') {
    mainBuildingLayer = buildBuildingLayerElement();
    if (!mainBuildingLayer) mainBuildingLayer = <div><div className="loading">Loading&#8230;</div></div>;
  }

  // --- Overlay layers from checkboxes ---
  const overlayLayers = LAYER_CONFIG.map(l => layerStates[l.key] ? buildBoundaryLayer(l.key) : null);
  const overlayBuildingLayer = buildingCheck ? buildBuildingLayerElement() : null;

  // Highlight the scoped user's own boundary (dashed blue outline) so they see
  // their working area. Admins (is_national) have no boundary -> nothing drawn.
  const scopeHighlight = (scope && !scope.is_national && scope.polygon) ? (
    <Polygon
      positions={scope.polygon.coordinates.map((poly) =>
        poly.map((ring) => ring.map(([lng, lat]) => [lat, lng]))
      )}
      pathOptions={{ color: '#2563eb', weight: 3, fillColor: '#2563eb', fillOpacity: 0.05, dashArray: '6 4' }}
    >
      <Popup>{scope.boundary_name} ({scope.level})</Popup>
    </Polygon>
  ) : null;

  // --- Single selected feature rendering ---
  let singlePolygon = null;
  let markerPoi = null;
  let circlePoi = null;

  if (dataOne && dataOne.length > 0) {
    if (dataType === 'building' || typeData === 'building') {
      singlePolygon = dataOne.map((dt) => (
        dt.building_polygon &&
        <Polygon key={dt.id} color="red" fillOpacity={1} fillColor="transparent" weight={1} positions={
          // GeoJSON MultiPolygon [poly][ring][pt][lng,lat] -> Leaflet [lat,lng] (non-mutating).
          dt.building_polygon.coordinates.map((poly) =>
            poly.map((ring) => ring.map(([lng, lat]) => [lat, lng]))
          )
        }>
          <Popup minWidth={350} maxHeight={700}>
            <BuildingDetailPopup dt={dt} memberCount={memberCounts ? memberCounts[dt.id] : undefined} />
          </Popup>
        </Polygon>
      ));

      markerPoi = dataOne.map((dt) => (
        dt.building_centroid ?
          <Marker key={`m-${dt.id}`} position={[dt.building_centroid.coordinates[1], dt.building_centroid.coordinates[0]]} icon={defaultIcon}>
            <Popup minWidth={350} maxHeight={700}>
              <BuildingDetailPopup dt={dt} memberCount={memberCounts ? memberCounts[dt.id] : undefined} />
            </Popup>
          </Marker> : null
      ));

      circlePoi = dataOne.map((pt) => (
        pt.building_centroid ?
          <Circle key={`c-${pt.id}`} fill="dodgerBlue" opacity={0.5} center={[pt.building_centroid.coordinates[1], pt.building_centroid.coordinates[0]]} radius={5}>
            <Popup ref={(r) => { popupRef = r; setRefReady(true); }}>
              {pt.brn}
            </Popup>
          </Circle> : null
      ));
    } else if (dataType === 'boundary' || typeData === 'boundary') {
      singlePolygon = dataOne.map((dt) => (
        dt.boundary_polygon &&
        <Polygon key={dt.id} color="red" fillOpacity={1} fillColor="transparent" weight={3} positions={
          // GeoJSON MultiPolygon [poly][ring][pt][lng,lat] -> Leaflet [lat,lng] (non-mutating).
          dt.boundary_polygon.coordinates.map((poly) =>
            poly.map((ring) => ring.map(([lng, lat]) => [lat, lng]))
          )
        }>
          <Popup ref={(r) => { popupRef = r; setRefReady(true); }}>
            {dt.boundary_name}
          </Popup>
        </Polygon>
      ));
    } else if (dataType === 'island') {
      singlePolygon = dataOne.map((dt) => (
        dt.island_polygon &&
        <Polygon key={dt.id} color="#810541" fillOpacity={0.08} fillColor="#810541" weight={2} positions={
          // GeoJSON MultiPolygon [poly][ring][pt][lng,lat] -> Leaflet [lat,lng]
          // (non-mutating: only swap the innermost coordinate pair).
          dt.island_polygon.coordinates.map((poly) =>
            poly.map((ring) => ring.map(([lng, lat]) => [lat, lng]))
          )
        }>
          <Popup ref={(r) => { popupRef = r; setRefReady(true); }}>
            {dt.name}
          </Popup>
        </Polygon>
      ));
      markerPoi = dataOne.map((dt) => (
        dt.island_centroid ?
          <Marker key={`im-${dt.id}`} position={[dt.island_centroid.coordinates[1], dt.island_centroid.coordinates[0]]} icon={defaultIcon}>
            <Popup>{dt.name}</Popup>
          </Marker> : null
      ));
    }
  }

  // Active layers array for legend
  const activeLayers = LAYER_CONFIG.map(l => layerStates[l.key] || mainKey === l.key);
  const showBuildings = buildingCheck || value === 6 || value === '6';

  // Aggregate stats for the legend summary card
  const legendStats = useMemo(() => {
    if (!building || !building.features) return null;
    const total = building.features.length;
    let members = 0;
    let anyCount = false;
    for (const f of building.features) {
      const c = memberCounts && memberCounts[f.properties.id];
      if (typeof c === 'number') { members += c; anyCount = true; }
    }
    return { total, members: anyCount ? members : null };
  }, [building, memberCounts]);

  return (
    <>
      {loading && <div><div className="loading"></div></div>}
      <div style={{ position: 'relative', width: '100%' }}>
        <MapContainer
          style={{ height: "100vh", width: "100%" }}
          ref={mapRef}
          className="map"
          whenCreated={(mapInstance) => (mapRef.current = mapInstance)}
          scrollWheelZoom
          center={[-6.1659, 39.2026]}
          zoom={11}
          minZoom={5}
          maxZoom={22}
          zoomControl={true}
        >
          <LayersControl position="bottomleft">
            {TILE_LAYERS.map((tile) => (
              <BaseLayer key={tile.name} name={tile.name} checked={tile.checked}>
                {/* maxNativeZoom lets the map zoom to 22 by upscaling tiles past
                    the provider's native level instead of going blank. */}
                <TileLayer url={tile.url} maxZoom={22} maxNativeZoom={20} />
              </BaseLayer>
            ))}
          </LayersControl>

          <ScaleControl position="bottomright" imperial={false} />

          {/* Crowding heatmap — driven by building centroids weighted by member count */}
          <HeatmapLayer
            buildings={building}
            memberCounts={memberCounts}
            visible={heatmapCheck}
          />

          <FeatureGroup>
            {singlePolygon}
            {markerPoi}
          </FeatureGroup>

          {/* OSM-style outline drawing for a single opened building */}
          {isSingleBuilding && (
            <BuildingDraw
              active={drawMode}
              polygon={existingPolygon}
              onChange={setDrawnGeoJson}
            />
          )}

          <FeatureGroup>
            {scopeHighlight}
            {mainLayer}
            {mainBuildingLayer}
            {overlayLayers}
            {overlayBuildingLayer}
          </FeatureGroup>

          {/* Filter boundary highlight */}
          {filterBoundary && filterBoundary.features && (
            <FeatureGroup>
              <GeoJSON
                key={`filter-${boundaryFilter}`}
                data={filterBoundary}
                style={() => ({ color: '#ff4444', weight: 3, fillColor: '#ff4444', fillOpacity: 0.05, dashArray: '8 4' })}
              />
            </FeatureGroup>
          )}
        </MapContainer>

        {/* Overlay controls container — sits above Leaflet's own z-index stacking context */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, pointerEvents: 'none' }}>

          {/* Building outline draw panel (only when a single building is opened) */}
          {isSingleBuilding && (
            <div style={{
              position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
              zIndex: 10003, pointerEvents: 'auto',
              background: '#ffffff', borderRadius: 10, padding: '8px 12px',
              boxShadow: '0 6px 18px rgba(15,23,42,0.15)', display: 'flex',
              alignItems: 'center', gap: 10,
            }}>
              <span className="fw-semibold text-dark">
                <i className="ri-shape-2-line me-1 text-primary"></i>
                Building Outline
              </span>
              {!drawMode ? (
                <button className="btn btn-sm btn-primary" onClick={() => setDrawMode(true)}>
                  <i className="ri-pencil-ruler-2-line me-1"></i>
                  {existingPolygon ? 'Edit outline' : 'Draw outline'}
                </button>
              ) : (
                <>
                  <span className="text-muted small">Use the polygon tool (top-right), then Save.</span>
                  <button className="btn btn-sm btn-success" onClick={saveDrawnPolygon} disabled={savingPolygon}>
                    {savingPolygon ? 'Saving…' : <><i className="ri-save-3-line me-1"></i>Save</>}
                  </button>
                  <button className="btn btn-sm btn-light" onClick={() => { setDrawMode(false); setDrawnGeoJson(null); }}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          )}

          {/* Floating map controls — top-left under Leaflet's own zoom buttons */}
          <div style={{
            position: 'absolute', top: 90, left: 12, zIndex: 10002,
            display: 'flex', flexDirection: 'column', gap: 6,
            pointerEvents: 'auto',
          }}>
            <button
              onClick={() => mapRef.current?.flyTo([-6.1659, 39.2026], 11)}
              style={{
                background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8,
                width: 34, height: 34, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 14,
                color: '#0f172a', boxShadow: '0 4px 10px rgba(15,23,42,0.08)',
              }}
              title="Fit to Zanzibar"
            >
              <i className="bi bi-house-fill"></i>
            </button>
            <button
              onClick={toggleFullscreen}
              style={{
                background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8,
                width: 34, height: 34, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 14,
                color: '#0f172a', boxShadow: '0 4px 10px rgba(15,23,42,0.08)',
              }}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              <i className={`bi bi-${isFullscreen ? 'fullscreen-exit' : 'arrows-fullscreen'}`}></i>
            </button>
          </div>

          {/* Layer overlay panel */}
          <LayerPanel
            layerStates={layerStates}
            onToggle={handleLayerToggle}
            buildingCheck={buildingCheck}
            onBuildingToggle={handleBuildingToggle}
            heatmapCheck={heatmapCheck}
            onHeatmapToggle={handleHeatmapToggle}
          />

          {/* Map legend */}
          <MapLegend
            activeLayers={activeLayers}
            showBuildings={showBuildings}
            showHeatmap={heatmapCheck}
            stats={legendStats}
          />
        </div>
      </div>
    </>
  );
};
